import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import { useCompany } from '../context/CompanyContext';
import { SkeletonDashboard } from '../components/common/Skeleton';
const fmtN = (n) => (n == null ? '—' : Number(n).toLocaleString());

/* ─── Color maps ─────────────────────────────────────────────── */
const ORDER_COLORS = {
  pending:    '#f59e0b',
  confirmed:  '#3b82f6',
  processing: '#8b5cf6',
  dispatched: '#06b6d4',
  delivered:  '#10b981',
  cancelled:  '#ef4444',
};

const SR_PRIORITY_COLORS = { urgent: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#10b981' };
const SR_STATUS_COLORS   = {
  open:             '#3b82f6',
  in_progress:      '#8b5cf6',
  pending_customer: '#f59e0b',
  resolved:         '#10b981',
  closed:           '#64748b',
  cancelled:        '#94a3b8',
};

const PIPE_META = {
  new:         { label: 'New',         color: '#3b82f6', bg: '#eff6ff' },
  quoted:      { label: 'Quoted',      color: '#8b5cf6', bg: '#f5f3ff' },
  negotiating: { label: 'Negotiating', color: '#f59e0b', bg: '#fffbeb' },
  won:         { label: 'Won',         color: '#10b981', bg: '#f0fdf4' },
  on_hold:     { label: 'On Hold',     color: '#64748b', bg: '#f8fafc' },
  lost:        { label: 'Lost',        color: '#ef4444', bg: '#fef2f2' },
};

const ROLE_META = {
  admin:     { label: 'Admin',      bg: '#fef3c7', color: '#92400e' },
  manager:   { label: 'Manager',    bg: '#ede9fe', color: '#5b21b6' },
  mr:        { label: 'Med. Rep',   bg: '#d1fae5', color: '#065f46' },
  bd:        { label: 'Biz Dev',    bg: '#dbeafe', color: '#1e40af' },
  scientist: { label: 'Scientist',  bg: '#fce7f3', color: '#9d174d' },
};

const WORKLOAD = (n) =>
  n === 0  ? { label: 'Free',     color: '#10b981', bg: '#d1fae5' } :
  n <= 3   ? { label: 'Moderate', color: '#f59e0b', bg: '#fef3c7' } :
  n <= 6   ? { label: 'Busy',     color: '#f97316', bg: '#ffedd5' } :
             { label: 'Heavy',    color: '#ef4444', bg: '#fee2e2' };

/* ─── KPI Card ───────────────────────────────────────────────── */
function KpiCard({ icon, label, value, sub, accent = '#1d4ed8' }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '22px 24px',
      border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: accent + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent }}>
          {icon}
        </div>
      </div>
      <div>
        <div style={{ fontSize: '1.7rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginTop: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
        {sub && <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 3 }}>{sub}</div>}
      </div>
    </div>
  );
}

/* ─── Revenue Bar Chart (SVG) ────────────────────────────────── */
function BarChart({ data, fmt = (n) => `$${Number(n).toFixed(0)}` }) {
  if (!data?.length) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#94a3b8', fontSize: '0.875rem' }}>
      No data for the last 6 months
    </div>
  );

  const W = 520, H = 210;
  const pad = { t: 24, r: 20, b: 44, l: 58 };
  const cW = W - pad.l - pad.r;
  const cH = H - pad.t - pad.b;
  const maxRev = Math.max(...data.map(d => d.revenue), 1);
  const n = data.length;
  const slot = cW / n;
  const barW = Math.min(42, slot * 0.6);
  const ticks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '100%' }}>
      {/* Gridlines */}
      {ticks.map(pct => {
        const y = pad.t + cH * (1 - pct);
        return (
          <g key={pct}>
            <line x1={pad.l} y1={y} x2={W - pad.r} y2={y}
              stroke={pct === 0 ? '#cbd5e1' : '#f1f5f9'} strokeWidth={pct === 0 ? 1.5 : 1} />
            <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize={10} fill="#94a3b8">
              {fmt(maxRev * pct)}
            </text>
          </g>
        );
      })}
      {/* Bars */}
      {data.map((d, i) => {
        const barH = maxRev > 0 ? (d.revenue / maxRev) * cH : 2;
        const x = pad.l + i * slot + (slot - barW) / 2;
        const y = pad.t + cH - barH;
        return (
          <g key={i}>
            {/* bg track */}
            <rect x={x} y={pad.t} width={barW} height={cH} rx={4} fill="#f8fafc" />
            {/* bar */}
            <rect x={x} y={y} width={barW} height={barH} rx={4} fill="#1d4ed8" />
            {/* order count above */}
            <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize={9} fill="#94a3b8">{d.orders > 0 ? d.orders : ''}</text>
            {/* month label */}
            <text x={x + barW / 2} y={H - pad.b + 16} textAnchor="middle" fontSize={10} fill="#64748b">{d.month}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ─── Order Status Donut (SVG) ───────────────────────────────── */
function DonutChart({ data }) {
  const SIZE = 200, r = 72, sw = 28;
  const cx = SIZE / 2, cy = SIZE / 2;
  const circ = 2 * Math.PI * r;
  const total = data.reduce((s, d) => s + (d.count || 0), 0);

  if (!total) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: SIZE, color: '#94a3b8', fontSize: '0.875rem' }}>
      No orders yet
    </div>
  );

  let cum = 0;
  const segs = data.filter(d => d.count > 0).map(d => {
    const dash = (d.count / total) * circ;
    const s = { ...d, dash, cum };
    cum += dash;
    return s;
  });

  return (
    <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ display: 'block' }}>
      {/* Track */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth={sw} />
      {/* Segments — rotated so drawing starts at 12 o'clock */}
      <g transform={`rotate(-90 ${cx} ${cy})`}>
        {segs.map((s, i) => (
          <circle key={i}
            cx={cx} cy={cy} r={r} fill="none"
            stroke={ORDER_COLORS[s.status] || '#94a3b8'}
            strokeWidth={sw}
            strokeDasharray={`${s.dash} ${circ - s.dash}`}
            strokeDashoffset={s.cum}
          />
        ))}
      </g>
      {/* Center labels */}
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize="28" fontWeight="900" fill="#0f172a">{total}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="11" fill="#94a3b8">Total Orders</text>
    </svg>
  );
}

/* ─── Avatar initial ─────────────────────────────────────────── */
function Avatar({ name, size = 34 }) {
  const initials = name?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
  const hue = (name?.charCodeAt(0) || 65) * 17 % 360;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `hsl(${hue},55%,92%)`, color: `hsl(${hue},55%,35%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.38, letterSpacing: '-0.5px',
    }}>{initials}</div>
  );
}

/* ─── Mini bar (inline percentage bar) ──────────────────────── */
function MiniBar({ value, max, color = '#1d4ed8' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ minWidth: 22, fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>{value}</span>
      <div style={{ flex: 1, height: 6, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', maxWidth: 80 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4 }} />
      </div>
    </div>
  );
}

/* ─── Period config ──────────────────────────────────────────── */
const PERIODS = [
  { key: 'daily',       label: 'Today' },
  { key: 'weekly',      label: '7 Days' },
  { key: 'monthly',     label: '1 Month' },
  { key: 'quarterly',   label: '3 Months' },
  { key: 'half_yearly', label: '6 Months' },
  { key: 'yearly',      label: '1 Year' },
  { key: '5_yearly',    label: '5 Years' },
  { key: '10_yearly',   label: '10 Years' },
  { key: 'all_time',    label: 'All Time' },
];

/* ══════════════════════════════════════════════════════════════ */
export default function Analytics() {
  const { isMobile } = useResponsive();
  const { companyCurrency, usdToInrRate } = useCompany();
  const [overview,    setOverview]    = useState(null);
  const [orders,      setOrders]      = useState(null);
  const [agents,      setAgents]      = useState([]);
  const [pipeline,    setPipeline]    = useState([]);
  const [srStats,     setSrStats]     = useState(null);
  const [projStats,   setProjStats]   = useState(null);
  const [coaStats,    setCoaStats]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,     setError]     = useState('');
  const [currency,  setCurrency]  = useState(companyCurrency);
  const [convRate,  setConvRate]  = useState(usdToInrRate);
  const [rateInput, setRateInput] = useState(String(usdToInrRate));
  const [period,    setPeriod]    = useState('all_time');

  // Sync defaults when company settings load
  useEffect(() => {
    setCurrency(companyCurrency);
    setConvRate(usdToInrRate);
    setRateInput(String(usdToInrRate));
  }, [companyCurrency, usdToInrRate]);

  const fmtMoney = (n, cur = currency, rate = convRate) => {
    if (n == null || isNaN(n)) return '—';
    const v = parseFloat(n) * (cur === 'INR' ? rate : 1);
    const CUR_SYM = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED ', SGD: 'S$', JPY: '¥' };
    const sym = CUR_SYM[cur] || `${cur} `;
    if (cur === 'INR') {
      if (v >= 10_000_000) return `${sym}${(v / 10_000_000).toFixed(1)}Cr`;
      if (v >= 100_000)    return `${sym}${(v / 100_000).toFixed(1)}L`;
      if (v >= 1_000)      return `${sym}${(v / 1_000).toFixed(1)}K`;
      return `${sym}${v.toFixed(0)}`;
    }
    if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000)     return `${sym}${(v / 1_000).toFixed(1)}K`;
    return `${sym}${v.toFixed(0)}`;
  };

  const fmt = (n) => fmtMoney(n, currency, convRate);

  useEffect(() => {
    setLoading(true);
    setError('');
    const q = period !== 'all_time' ? `?period=${period}` : '';
    Promise.all([
      api.get(`/analytics/overview${q}`),
      api.get(`/analytics/orders${q}`),
      api.get(`/analytics/agents${q}`),
      api.get(`/analytics/pipeline${q}`),
      api.get(`/analytics/service-requests${q}`),
      api.get(`/analytics/projects${q}`),
      api.get(`/analytics/coa${q}`),
    ]).then(([ov, ord, ag, pip, sr, proj, coa]) => {
      setOverview(ov.data.data);
      setOrders(ord.data.data);
      setAgents(ag.data.data);
      setPipeline(pip.data.data);
      setSrStats(sr.data.data);
      setProjStats(proj.data.data);
      setCoaStats(coa.data.data);
    }).catch(() => setError('Failed to load analytics. Please try again.'))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <SkeletonDashboard />;

  if (error) return (
    <div style={{ maxWidth: 500, margin: '80px auto', textAlign: 'center', padding: '0 24px' }}>
      <div style={{ background: '#fee2e2', borderRadius: 12, padding: '20px 24px', color: '#dc2626', fontSize: '0.9rem' }}>{error}</div>
    </div>
  );

  /* derived */
  const ov = overview || {};
  const maxAgentItems = Math.max(...agents.map(a => a.total_items), 1);
  const maxPipeCount  = Math.max(...pipeline.map(p => p.count), 1);
  const orderStatusData = orders?.status_breakdown || [];

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh' }}>

      {/* ── Page header ──────────────────────────────────────── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: isMobile ? '20px 16px 0' : '24px 32px 0' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 900, color: '#0f172a', marginBottom: 4, letterSpacing: '-0.4px' }}>Analytics</h1>
            <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Financial performance, order pipeline, and team workload overview</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Currency toggle */}
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
              {['INR', 'USD'].map(cur => (
                <button key={cur} onClick={() => setCurrency(cur)} style={{
                  padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700,
                  background: currency === cur ? '#fff' : 'transparent',
                  color: currency === cur ? '#0f172a' : '#64748b',
                  boxShadow: currency === cur ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s',
                }}>
                  {cur === 'INR' ? '₹ INR' : '$ USD'}
                </button>
              ))}
            </div>
            {/* Rate input — only when INR */}
            {currency === 'INR' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 12px' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap' }}>1 USD =</span>
                <input
                  type="number" min="1" step="0.01"
                  value={rateInput}
                  onChange={e => {
                    setRateInput(e.target.value);
                    const v = parseFloat(e.target.value);
                    if (v > 0) setConvRate(v);
                  }}
                  style={{ width: 64, border: 'none', outline: 'none', fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', textAlign: 'right', background: 'transparent' }}
                />
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>₹</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Period selector bar ───────────────────────────── */}
        <div style={{ maxWidth: 1280, margin: '0 auto', overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: 2, paddingBottom: 0, minWidth: 'max-content' }}>
            {PERIODS.map(p => {
              const active = period === p.key;
              return (
                <button key={p.key} onClick={() => setPeriod(p.key)} style={{
                  padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: active ? 700 : 500,
                  background: 'transparent', color: active ? '#1d4ed8' : '#64748b',
                  borderBottom: active ? '2.5px solid #1d4ed8' : '2.5px solid transparent',
                  transition: 'all 0.15s', whiteSpace: 'nowrap',
                }}>
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: isMobile ? '20px 16px 48px' : '32px 32px 64px' }}>

        {/* ── KPI Cards ──────────────────────────────────────── */}
        {(() => {
          const pLabel = PERIODS.find(p => p.key === period)?.label || 'All Time';
          const periodSub = period === 'all_time' ? 'all-time' : pLabel.toLowerCase();
          return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 18, marginBottom: 32 }}>
          <KpiCard
            label="Revenue"
            value={fmt(ov.total_revenue)}
            sub={`${periodSub}`}
            accent="#1d4ed8"
            icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}
          />
          <KpiCard
            label="Orders"
            value={fmtN(ov.total_orders)}
            sub={`${periodSub}`}
            accent="#059669"
            icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>}
          />
          <KpiCard
            label="Avg. Order Value"
            value={fmt(ov.avg_order_value)}
            sub={`per order · ${periodSub}`}
            accent="#7c3aed"
            icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>}
          />
          <KpiCard
            label="Win Rate"
            value={`${ov.win_rate ?? '—'}%`}
            sub="inquiries closed won"
            accent="#0891b2"
            icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}
          />
          <KpiCard
            label="Pipeline Value"
            value={fmt(ov.pipeline_value)}
            sub={`${fmtN(ov.open_inquiries)} open inquiries`}
            accent="#f59e0b"
            icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
          />
          <KpiCard
            label="Pending Orders"
            value={fmtN(ov.pending_orders)}
            sub={`awaiting delivery · ${periodSub}`}
            accent="#ef4444"
            icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
          />
        </div>
          );
        })()}

        {/* ── Revenue Trend + Order Status ───────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: 20, marginBottom: 24 }}>

          {/* Revenue trend */}
          <div style={{ background: '#fff', borderRadius: 16, padding: '24px 28px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.7rem', fontWeight: 700, padding: '3px 12px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Revenue Trend</div>
              <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>Orders & Revenue — {PERIODS.find(p => p.key === period)?.label || 'All Time'}</h3>
              <p style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Numbers above bars show order count</p>
            </div>
            <div style={{ height: 210 }}>
              <BarChart data={orders?.monthly_trend || []} fmt={fmt} />
            </div>
          </div>

          {/* Order status donut */}
          <div style={{ background: '#fff', borderRadius: 16, padding: '24px 24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'inline-block', background: '#f5f3ff', color: '#7c3aed', fontSize: '0.7rem', fontWeight: 700, padding: '3px 12px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Order Status</div>
              <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>Status Breakdown</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <DonutChart data={orderStatusData} />
              {/* Legend */}
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 7 }}>
                {orderStatusData.map(d => (
                  <div key={d.status} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: ORDER_COLORS[d.status] || '#94a3b8', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.78rem', color: '#374151', textTransform: 'capitalize' }}>{d.status}</span>
                    </div>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>{d.count} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({fmt(d.value)})</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Inquiry Pipeline ───────────────────────────────── */}
        <div style={{ background: '#fff', borderRadius: 16, padding: '24px 28px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', marginBottom: 24 }}>
          <div style={{ marginBottom: 22 }}>
            <div style={{ display: 'inline-block', background: '#fffbeb', color: '#92400e', fontSize: '0.7rem', fontWeight: 700, padding: '3px 12px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Sales Pipeline</div>
            <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>Inquiry Stage Distribution</h3>
            <p style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Count and quoted value at each stage · avg. age in days</p>
          </div>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: isMobile ? 560 : 'auto' }}>
            {pipeline.map(s => {
              const meta = PIPE_META[s.status] || { label: s.status, color: '#94a3b8', bg: '#f8fafc' };
              const pct = maxPipeCount > 0 ? Math.round((s.count / maxPipeCount) * 100) : 0;
              return (
                <div key={s.status} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 80px 80px 60px', alignItems: 'center', gap: 16 }}>
                  {/* Stage label */}
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: meta.bg, color: meta.color, textAlign: 'center' }}>
                    {meta.label}
                  </span>
                  {/* Bar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, height: 10, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: meta.color, borderRadius: 6, transition: 'width 0.4s ease' }} />
                    </div>
                  </div>
                  {/* Count */}
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>{s.count}</span>
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: 3 }}>deals</span>
                  </div>
                  {/* Value */}
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151' }}>{fmt(s.value)}</span>
                  </div>
                  {/* Avg age */}
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{s.avg_age}d</span>
                  </div>
                </div>
              );
            })}
            {pipeline.length === 0 && (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: '0.875rem' }}>No inquiry data</div>
            )}
          </div>
          </div>
        </div>

        {/* ── Agent Workload Table ────────────────────────────── */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
          <div style={{ padding: '24px 28px 16px' }}>
            <div style={{ display: 'inline-block', background: '#f0fdf4', color: '#065f46', fontSize: '0.7rem', fontWeight: 700, padding: '3px 12px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Team Workload</div>
            <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>Pending Items by Assignee</h3>
            <p style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Orders in progress, open inquiries, and active projects per team member</p>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
                  {['Team Member', 'Role', 'Pending Orders', 'Open Inquiries', 'Active Projects', 'Total Pipeline', 'Workload'].map(h => (
                    <th key={h} style={{ padding: '10px 20px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {agents.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>No team data</td></tr>
                ) : agents.map((a, idx) => {
                  const roleMeta = ROLE_META[a.role] || { label: a.role, bg: '#f8fafc', color: '#374151' };
                  const wl = WORKLOAD(a.total_items);
                  return (
                    <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      {/* Name */}
                      <td style={{ padding: '14px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar name={a.name} />
                          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>{a.name}</span>
                        </div>
                      </td>
                      {/* Role */}
                      <td style={{ padding: '14px 20px' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: roleMeta.bg, color: roleMeta.color }}>
                          {roleMeta.label}
                        </span>
                      </td>
                      {/* Pending Orders */}
                      <td style={{ padding: '14px 20px' }}>
                        <MiniBar value={a.pending_orders} max={maxAgentItems} color={ORDER_COLORS.pending} />
                        {a.orders_value > 0 && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 3, paddingLeft: 30 }}>{fmt(a.orders_value)}</div>}
                      </td>
                      {/* Open Inquiries */}
                      <td style={{ padding: '14px 20px' }}>
                        <MiniBar value={a.open_inquiries} max={maxAgentItems} color="#8b5cf6" />
                        {a.inq_value > 0 && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 3, paddingLeft: 30 }}>{fmt(a.inq_value)}</div>}
                      </td>
                      {/* Active Projects */}
                      <td style={{ padding: '14px 20px' }}>
                        <MiniBar value={a.active_projects} max={maxAgentItems} color="#0891b2" />
                        {a.proj_budget > 0 && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 3, paddingLeft: 30 }}>{fmt(a.proj_budget)}</div>}
                      </td>
                      {/* Total Pipeline */}
                      <td style={{ padding: '14px 20px' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>{fmt(a.total_value)}</div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{a.total_items} item{a.total_items !== 1 ? 's' : ''} pending</div>
                      </td>
                      {/* Workload */}
                      <td style={{ padding: '14px 20px' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: wl.bg, color: wl.color }}>
                          {wl.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Table footer */}
          {agents.length > 0 && (
            <div style={{ padding: '12px 24px', borderTop: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                Workload: &nbsp;
                {[0, 1, 4, 7].map((n, i) => {
                  const labels = ['Free (0)', 'Moderate (1–3)', 'Busy (4–6)', 'Heavy (7+)'];
                  const colors = ['#10b981', '#f59e0b', '#f97316', '#ef4444'];
                  return (
                    <span key={n} style={{ marginRight: 14 }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: colors[i], marginRight: 4, verticalAlign: 'middle' }} />
                      {labels[i]}
                    </span>
                  );
                })}
              </span>
            </div>
          )}
        </div>

      {/* ── Service Requests ────────────────────────────────── */}
      {srStats && (() => {
        const t = srStats.totals || {};
        const resolutionLabel = t.avg_resolution_hours != null
          ? t.avg_resolution_hours >= 24
            ? `${(t.avg_resolution_hours / 24).toFixed(1)}d avg`
            : `${t.avg_resolution_hours}h avg`
          : '—';
        const maxStatus   = Math.max(...(srStats.by_status   || []).map(r => r.count), 1);
        const maxPriority = Math.max(...(srStats.by_priority || []).map(r => r.count), 1);
        const maxType     = Math.max(...(srStats.by_type     || []).map(r => r.count), 1);

        return (
          <div style={{ marginTop: 24 }}>
            {/* SR section header */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'inline-block', background: '#fef2f2', color: '#dc2626', fontSize: '0.7rem', fontWeight: 700, padding: '3px 12px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Service Requests</div>
              <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>Support & Maintenance Overview</h3>
            </div>

            {/* SR KPI cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 20 }}>
              <KpiCard label="Total SRs" value={t.total ?? 0} sub={`${PERIODS.find(p => p.key === period)?.label || 'All Time'}`} accent="#dc2626"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5z"/><path d="M20.5 10H19V8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/><path d="M9.5 14c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5S8 21.33 8 20.5v-5c0-.83.67-1.5 1.5-1.5z"/><path d="M3.5 14H5v1.5c0 .83-.67 1.5-1.5 1.5S2 16.33 2 15.5 2.67 14 3.5 14z"/><path d="M14 14.5c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5z"/><path d="M15.5 19H14v1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/><path d="M10 9.5C10 8.67 9.33 8 8.5 8h-5C2.67 8 2 8.67 2 9.5S2.67 11 3.5 11h5c.83 0 1.5-.67 1.5-1.5z"/><path d="M8.5 5H10V3.5C10 2.67 9.33 2 8.5 2S7 2.67 7 3.5 7.67 5 8.5 5z"/></svg>} />
              <KpiCard label="Open" value={t.open_count ?? 0} sub={`${t.urgent_open ?? 0} urgent`} accent="#f97316"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>} />
              <KpiCard label="Resolved" value={t.resolved_count ?? 0} sub="closed or resolved" accent="#10b981"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>} />
              <KpiCard label="Avg Resolution" value={resolutionLabel} sub="time to close" accent="#0891b2"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>} />
            </div>

            {/* SR breakdown: Status + Priority + Type */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16 }}>
              {/* By Status */}
              <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>By Status</div>
                {(srStats.by_status || []).length === 0
                  ? <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No data</div>
                  : (srStats.by_status || []).map(r => (
                    <div key={r.status} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', textTransform: 'capitalize' }}>{r.status.replace(/_/g, ' ')}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>{r.count}</span>
                      </div>
                      <div style={{ height: 6, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round((r.count / maxStatus) * 100)}%`, height: '100%', background: SR_STATUS_COLORS[r.status] || '#94a3b8', borderRadius: 4, transition: 'width 0.4s' }} />
                      </div>
                    </div>
                  ))
                }
              </div>

              {/* By Priority */}
              <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#f97316', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>By Priority</div>
                {(srStats.by_priority || []).length === 0
                  ? <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No data</div>
                  : (srStats.by_priority || []).map(r => (
                    <div key={r.priority} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <span style={{ width: 9, height: 9, borderRadius: '50%', background: SR_PRIORITY_COLORS[r.priority] || '#94a3b8', flexShrink: 0, display: 'inline-block' }} />
                          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', textTransform: 'capitalize' }}>{r.priority}</span>
                        </div>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>{r.count} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({r.open_count} open)</span></span>
                      </div>
                      <div style={{ height: 6, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round((r.count / maxPriority) * 100)}%`, height: '100%', background: SR_PRIORITY_COLORS[r.priority] || '#94a3b8', borderRadius: 4, transition: 'width 0.4s' }} />
                      </div>
                    </div>
                  ))
                }
              </div>

              {/* By Type */}
              <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0891b2', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>By Type</div>
                {(srStats.by_type || []).length === 0
                  ? <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No data</div>
                  : (srStats.by_type || []).map((r, i) => (
                    <div key={r.type} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', textTransform: 'capitalize' }}>{r.type?.replace(/_/g, ' ') || '—'}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>{r.count}</span>
                      </div>
                      <div style={{ height: 6, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round((r.count / maxType) * 100)}%`, height: '100%',
                          background: ['#1d4ed8','#7c3aed','#0891b2','#059669','#dc2626','#f59e0b','#f97316','#6366f1'][i % 8],
                          borderRadius: 4, transition: 'width 0.4s' }} />
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Projects Analytics ──────────────────────────────── */}
      {projStats && (() => {
        const t = projStats.totals || {};
        const statusItems = [
          { key: 'active',    label: 'Active',    color: '#10b981', value: t.active },
          { key: 'proposed',  label: 'Proposed',  color: '#3b82f6', value: t.proposed },
          { key: 'on_hold',   label: 'On Hold',   color: '#f59e0b', value: t.on_hold },
          { key: 'completed', label: 'Completed', color: '#64748b', value: t.completed },
          { key: 'frozen',    label: 'Frozen',    color: '#ef4444', value: t.frozen },
        ];
        const maxStatus   = Math.max(...statusItems.map(s => s.value || 0), 1);
        const maxType     = Math.max(...(projStats.by_type     || []).map(r => r.count), 1);
        const maxPriority = Math.max(...(projStats.by_priority || []).map(r => r.count), 1);
        const PRIORITY_COLORS = { critical:'#ef4444', high:'#f97316', medium:'#f59e0b', low:'#10b981' };
        const TYPE_COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#f97316','#8b5cf6','#ec4899','#14b8a6'];
        return (
          <div style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'inline-block', background: '#eef2ff', color: '#4338ca', fontSize: '0.7rem', fontWeight: 700, padding: '3px 12px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Projects</div>
              <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>Project Portfolio Overview</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 16, marginBottom: 20 }}>
              <KpiCard label="Total Projects" value={t.total ?? 0} sub={`${PERIODS.find(p => p.key === period)?.label || 'All Time'}`} accent="#6366f1"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>} />
              <KpiCard label="Active" value={t.active ?? 0} sub={`${t.proposed ?? 0} proposed`} accent="#10b981"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>} />
              <KpiCard label="Completed" value={t.completed ?? 0} sub="delivered" accent="#0891b2"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>} />
              <KpiCard label="Pipeline Budget" value={fmt(t.pipeline_budget)} sub="active + proposed" accent="#f59e0b"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16 }}>
              <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>By Status</div>
                {statusItems.map(s => (
                  <div key={s.key} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>{s.label}</span>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>{s.value ?? 0}</span>
                    </div>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.round(((s.value||0)/maxStatus)*100)}%`, height: '100%', background: s.color, borderRadius: 4, transition: 'width 0.4s' }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0891b2', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>By Type</div>
                {(projStats.by_type || []).length === 0
                  ? <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No data</div>
                  : (projStats.by_type || []).map((r, i) => (
                    <div key={r.type} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', textTransform: 'capitalize' }}>{(r.type||'').replace(/_/g,' ')}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>{r.count} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({fmt(r.budget)})</span></span>
                      </div>
                      <div style={{ height: 6, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round((r.count/maxType)*100)}%`, height: '100%', background: TYPE_COLORS[i%TYPE_COLORS.length], borderRadius: 4, transition: 'width 0.4s' }} />
                      </div>
                    </div>
                  ))}
              </div>
              <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#f97316', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>By Priority</div>
                {(projStats.by_priority || []).length === 0
                  ? <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No data</div>
                  : (projStats.by_priority || []).map(r => (
                    <div key={r.priority} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <span style={{ width: 9, height: 9, borderRadius: '50%', background: PRIORITY_COLORS[r.priority]||'#94a3b8', display: 'inline-block', flexShrink: 0 }} />
                          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', textTransform: 'capitalize' }}>{r.priority}</span>
                        </div>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>{r.count}</span>
                      </div>
                      <div style={{ height: 6, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round((r.count/maxPriority)*100)}%`, height: '100%', background: PRIORITY_COLORS[r.priority]||'#94a3b8', borderRadius: 4, transition: 'width 0.4s' }} />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── COA Analytics ───────────────────────────────────── */}
      {coaStats && (() => {
        const t = coaStats.totals || {};
        const maxProduct = Math.max(...(coaStats.by_product || []).map(r => r.count), 1);
        const maxAnalyst = Math.max(...(coaStats.by_analyst || []).map(r => r.count), 1);
        return (
          <div style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'inline-block', background: '#f0fdf4', color: '#065f46', fontSize: '0.7rem', fontWeight: 700, padding: '3px 12px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>COA</div>
              <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>Certificate of Analysis Overview</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 16, marginBottom: 20 }}>
              <KpiCard label="Total COAs" value={t.total ?? 0} sub={`${PERIODS.find(p => p.key === period)?.label || 'All Time'}`} accent="#059669"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>} />
              <KpiCard label="Approved" value={t.approved ?? 0} sub={`${t.approval_rate ?? 0}% approval rate`} accent="#10b981"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>} />
              <KpiCard label="Drafts" value={t.draft ?? 0} sub="pending approval" accent="#f59e0b"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>} />
              <KpiCard label="Unique Products" value={t.unique_products ?? 0} sub={`${t.active_analysts ?? 0} analyst${t.active_analysts !== 1 ? 's' : ''}`} accent="#0891b2"
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg>} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
              <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Top Products by COA Count</div>
                {(coaStats.by_product || []).length === 0
                  ? <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No data</div>
                  : (coaStats.by_product || []).map((r, i) => (
                    <div key={i} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{r.product_name}</div>
                          {r.catalog_number && <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{r.catalog_number}</div>}
                        </div>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', flexShrink: 0, marginLeft: 8 }}>{r.count}</span>
                      </div>
                      <div style={{ height: 5, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round((r.count/maxProduct)*100)}%`, height: '100%', background: '#10b981', borderRadius: 4 }} />
                      </div>
                    </div>
                  ))}
              </div>
              <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0891b2', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>COAs by Analyst</div>
                {(coaStats.by_analyst || []).length === 0
                  ? <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No data</div>
                  : (coaStats.by_analyst || []).map((r, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <Avatar name={r.name} size={30} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>{r.name}</span>
                          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>{r.count} <span style={{ color: '#10b981', fontWeight: 600 }}>({r.approved} ✓)</span></span>
                        </div>
                        <div style={{ height: 5, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.round((r.count/maxAnalyst)*100)}%`, height: '100%', background: '#0891b2', borderRadius: 4 }} />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        );
      })()}

      </div>
    </div>
  );
}
